/**
 * AlurackPanelTabExtra — Owns the entire "panel block" inside the 3D
 * library's Module tab. The library hands us full control via the
 * `panelTabExtra` slot so we can replicate the legacy TabModule layout
 * exactly (brand → panel/model → grid pairs of W/L and D/Weight → Power).
 *
 * Uses react-hook-form for local UI state, identical to the legacy
 * AddSegmentFormContainer / TabModule. Each change is pushed to the
 * library's Zustand store (the source of truth) via the slot's
 * `updateSelectSetting` (string / boolean fields) or `updateSetting`
 * (numeric fields, which the library validates as numbers).
 *
 * The legacy backend stores the panel BRAND by name (e.g. "Trina Solar")
 * and the panel by id, so brand options use `value: name` to match.
 */
import React, { useEffect, useMemo, useRef } from 'react';

import FormHelperText from '@mui/material/FormHelperText';
import { useSolarStore } from '@sunwisesoftware/pv-layout-core';
import { Grid, Stack } from '@sunwisesoftware/sunwise-ui';
import isEmpty from 'lodash/isEmpty';
import PropTypes from 'prop-types';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

import {
    ReactHookFormInput,
    ReactHookFormIntlNumberInput,
    ReactHookFormSelect,
} from 'common/components/form/bootstrap';

import * as createProposalSelectors from '../../createProposalModal/selectors';

const _toMm = (m) =>
    m == null || m === '' ? '' : Math.round(Number(m) * 1000);
const _fromMm = (mm) => {
    if (mm === '' || mm == null) return null;
    const n = Number(mm);
    return Number.isFinite(n) ? n / 1000 : null;
};

function AlurackPanelTabExtra({
    panelBrands,
    // Slot props from the library:
    onPanelSelect,
    panelOffer,
    selectedSettings,
    updateSelectSetting,
    updateSetting,
}) {
    const { t } = useTranslation();

    // ------------------------------------------------------------------
    // Form state (react-hook-form). The library's settings are the source
    // of truth; this form only mirrors them for the controlled inputs.
    // We seed default values at mount and reset whenever the selected
    // segment changes (detected by `selectedPanelCode` shift, which is
    // unique per roof in practice).
    // ------------------------------------------------------------------
    const defaultValues = useMemo(
        () => ({
            brandName: selectedSettings?.brandName ?? '',
            selectedPanelCode: selectedSettings?.selectedPanelCode ?? '',
            is_custom_module: selectedSettings?.is_custom_module === true,
            panel: {
                name: selectedSettings?.panel_name ?? '',
                width: _toMm(selectedSettings?.panelWidth),
                height: _toMm(selectedSettings?.panelHeight),
                depth: _toMm(selectedSettings?.panelDepth),
                watts: selectedSettings?.panelWatts ?? 0,
                weight: selectedSettings?.panel_weight ?? 0,
            },
        }),
        [],
    );

    const { control, reset, setValue } = useForm({ defaultValues });

    // Sync settings → form when the selected roof changes. We detect a
    // segment swap by comparing `selectedPanelCode` (a stable per-roof
    // identifier in practice). User edits go form → settings via the
    // onChange handlers and don't trigger this reset.
    const lastCodeRef = useRef(selectedSettings?.selectedPanelCode);
    useEffect(() => {
        const currentCode = selectedSettings?.selectedPanelCode;
        if (currentCode === lastCodeRef.current) return;
        lastCodeRef.current = currentCode;
        // Clear any stale panel-required error from the previous segment.
        useSolarStore.getState().updateFieldError('selectedPanelCode', null);
        reset({
            brandName: selectedSettings?.brandName ?? '',
            selectedPanelCode: selectedSettings?.selectedPanelCode ?? '',
            is_custom_module: selectedSettings?.is_custom_module === true,
            panel: {
                name: selectedSettings?.panel_name ?? '',
                width: _toMm(selectedSettings?.panelWidth),
                height: _toMm(selectedSettings?.panelHeight),
                depth: _toMm(selectedSettings?.panelDepth),
                watts: selectedSettings?.panelWatts ?? 0,
                weight: selectedSettings?.panel_weight ?? 0,
            },
        });
    }, [selectedSettings, reset]);

    const [brandName, isCustomFlag, watchedPanelCode] = useWatch({
        control,
        name: ['brandName', 'is_custom_module', 'selectedPanelCode'],
    });
    const isCustomModule = brandName === 'custom' || isCustomFlag === true;

    // Panel is required when a stock brand is chosen but no panel is selected.
    const panelSelectError =
        !isCustomModule && !isEmpty(brandName) && isEmpty(watchedPanelCode)
            ? t('Panel required')
            : null;

    // Keep the library's fieldErrors in sync so the Save button is disabled
    // whenever no panel is selected. updateFieldError(key, null) clears it.
    useEffect(() => {
        useSolarStore
            .getState()
            .updateFieldError('selectedPanelCode', panelSelectError ?? null);
    }, [panelSelectError]);

    // ------------------------------------------------------------------
    // Persistence — push form changes to the library Zustand store.
    // String / boolean fields go through `updateSelectSetting` (the
    // library's `updateSetting` validates as Number(...) and rejects
    // non-numeric values, which is why brand changes used to be lost).
    // ------------------------------------------------------------------
    const persistString = (key, value) => {
        updateSelectSetting(key)({ target: { value } });
    };
    const persistNumber = (key, value) => {
        if (value == null || value === '') return;
        const parsed = Number(value);
        if (!Number.isFinite(parsed)) return;
        updateSetting(key)({ target: { value: parsed } });
    };

    const onChangeBrand = (e) => {
        const next = e.target.value || '';
        persistString('brandName', next);
        if (next === 'custom') {
            persistString('is_custom_module', true);
            // Drop the stock-panel reference so the library treats this
            // as a custom panel.
            persistString('selectedPanelCode', '');
            setValue('selectedPanelCode', '');
            setValue('is_custom_module', true);
            // ALWAYS reset to legacy custom defaults (matching the legacy
            // TabModule onChangeBrand). Without overwriting, the previous
            // panel's name + dimensions linger in settings, which makes
            // SegmentSummary keep showing the OLD panel even though the
            // user picked Custom.
            persistString('panel_name', t('Custom'));
            setValue('panel.name', t('Custom'));
            persistNumber('panelWidth', 0.96);
            setValue('panel.width', 960);
            persistNumber('panelHeight', 1.6);
            setValue('panel.height', 1600);
            persistNumber('panelDepth', 0.035);
            setValue('panel.depth', 35);
            persistNumber('panelWatts', 225);
            setValue('panel.watts', 225);
            persistNumber('panel_weight', 20);
            setValue('panel.weight', 20);
        } else {
            persistString('is_custom_module', false);
            persistString('selectedPanelCode', '');
            setValue('selectedPanelCode', '');
            setValue('is_custom_module', false);
        }
    };

    const onChangePanel = (e) => {
        const code = e.target.value;
        // Library handler — reads the panel's dimensions from `panelOffer`
        // and writes width/height/depth/watts to the store.
        onPanelSelect?.(code);
        // Mirror those into the form so the W/L/D/W inputs reflect them.
        const panel = panelOffer?.find(
            (p) => p.panel_company_branch_office === code,
        );
        if (panel) {
            persistString('selectedPanelCode', code);
            persistString('panel_name', panel.name ?? '');
            const w = panel.weight ?? panel.peso ?? null;
            if (w != null) persistNumber('panel_weight', Number(w));
            // Keep the form mirrors in lockstep with the store.
            setValue('selectedPanelCode', code);
            setValue('panel.name', panel.name ?? '');
            setValue('panel.width', _toMm(panel.width / 1000));
            setValue('panel.height', _toMm(panel.height / 1000));
            setValue('panel.depth', _toMm(panel.depth / 1000));
            setValue('panel.watts', panel.watts ?? 0);
            if (w != null) setValue('panel.weight', Number(w));
        }
    };

    const brandOptions = useMemo(() => {
        // Mirrors legacy TabModule: brands followed by a fixed "Custom"
        // entry (always present, regardless of the library's
        // `allowCustomPanel` flag — Alurack always offers the custom
        // panel option).
        const items = (Array.isArray(panelBrands) ? panelBrands : []).map(
            (b) => ({ label: b.name, value: b.name }),
        );
        return [
            { disabled: true, label: t('Select'), value: '' },
            ...items,
            { label: t('Custom'), value: 'custom' },
        ];
    }, [panelBrands, t]);

    const panelOptions = useMemo(() => {
        const items = (Array.isArray(panelOffer) ? panelOffer : []).map(
            (p) => ({
                label: p.name,
                value: p.panel_company_branch_office,
            }),
        );
        return [{ disabled: true, label: t('Select'), value: '' }, ...items];
    }, [panelOffer, t]);

    return (
        <Stack spacing={2}>
            {/* 1 — Panel brand */}
            <ReactHookFormSelect
                control={control}
                isClearable={false}
                label={t('Panel brand')}
                name="brandName"
                onChange={onChangeBrand}
                options={brandOptions}
            />

            {/* 2 — Panel select (stock) OR Model input (custom) */}
            {!isCustomModule ? (
                <div>
                    <ReactHookFormSelect
                        autoComplete="off"
                        control={control}
                        disabled={isEmpty(brandName) || brandName === 'custom'}
                        label={t('Panel')}
                        name="selectedPanelCode"
                        onChange={onChangePanel}
                        options={panelOptions}
                    />
                    {panelSelectError && (
                        <FormHelperText error>
                            {panelSelectError}
                        </FormHelperText>
                    )}
                </div>
            ) : (
                <ReactHookFormInput
                    control={control}
                    fullWidth
                    label={t('Model')}
                    name="panel.name"
                    style={{ margin: 0 }}
                    //FormHelperTextProps={{ style: { margin: 0 } }}
                    onChange={(e) =>
                        persistString('panel_name', e.target.value)
                    }
                />
            )}

            {/* 3 — Width + Length side by side */}
            <Grid container spacing={2}>
                <Grid size="grow">
                    <ReactHookFormIntlNumberInput
                        append="mm"
                        control={control}
                        disabled={!isCustomModule}
                        fullWidth
                        label={t('Width')}
                        name="panel.width"
                        onChange={(e) => {
                            const v = _fromMm(e.target.value);
                            if (v != null) persistNumber('panelWidth', v);
                        }}
                        placeholder={t('Width')}
                    />
                </Grid>
                <Grid size="grow">
                    <ReactHookFormIntlNumberInput
                        append="mm"
                        control={control}
                        disabled={!isCustomModule}
                        fullWidth
                        label={t('Length')}
                        name="panel.height"
                        onChange={(e) => {
                            const v = _fromMm(e.target.value);
                            if (v != null) persistNumber('panelHeight', v);
                        }}
                        placeholder={t('Length')}
                    />
                </Grid>
            </Grid>

            {/* 4 — Depth + Weight side by side */}
            <Grid container spacing={2}>
                <Grid size="grow">
                    <ReactHookFormIntlNumberInput
                        append="mm"
                        control={control}
                        disabled={!isCustomModule}
                        fullWidth
                        label={t('Depth')}
                        name="panel.depth"
                        onChange={(e) => {
                            const v = _fromMm(e.target.value);
                            if (v != null) persistNumber('panelDepth', v);
                        }}
                        placeholder={t('Depth')}
                    />
                </Grid>
                <Grid size="grow">
                    <ReactHookFormIntlNumberInput
                        append="Kg"
                        control={control}
                        disabled={!isCustomModule}
                        fullWidth
                        label={t('Weight')}
                        name="panel.weight"
                        onChange={(e) =>
                            persistNumber('panel_weight', e.target.value)
                        }
                        placeholder={t('Weight')}
                    />
                </Grid>
            </Grid>

            {/* 5 — Power, full width */}
            <ReactHookFormIntlNumberInput
                append="w"
                control={control}
                disabled={!isCustomModule}
                fullWidth
                label={t('Power')}
                min="0"
                name="panel.watts"
                onChange={(e) => persistNumber('panelWatts', e.target.value)}
                placeholder={t('Power')}
            />
        </Stack>
    );
}

AlurackPanelTabExtra.propTypes = {
    onPanelSelect: PropTypes.func,
    panelBrands: PropTypes.array,
    panelOffer: PropTypes.array,
    selectedSettings: PropTypes.object,
    updateSelectSetting: PropTypes.func.isRequired,
    updateSetting: PropTypes.func.isRequired,
    // fieldErrors is injected by the library's panelTabExtra slot but
    // validation state is managed via useSolarStore directly here.
};

const mapStateToProps = createStructuredSelector({
    panelBrands: createProposalSelectors.getPanelBrandsData,
});

export default connect(mapStateToProps)(AlurackPanelTabExtra);
